import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Field, Notice, OnOffBadge, Switch } from '@/admin/AdminShell'
import { fetchSpeechKit, saveSpeechKit, type SpeechKitSettings } from '@/ai/summaryApi'
import { ApiError } from '@/lib/api'

/**
 * Подключение Yandex SpeechKit для расшифровки звонков (тикет #130).
 *
 * Отдельно от провайдера ИИ: тот может быть GigaChat, а распознавание — это своя роль
 * сервисного аккаунта и свой счёт. Ключ только записывается: пустое поле оставляет прежний.
 */
export function SpeechKitPanel() {
  const { t, user } = useApp()
  const id = useId()
  const queryClient = useQueryClient()
  const mayEdit = can(user, 'system.integration', 'update')
  const settings = useQuery({ queryKey: ['admin-speechkit'], queryFn: ({ signal }) => fetchSpeechKit(signal) })
  const [draft, setDraft] = useState<SpeechKitSettings | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (settings.data) setDraft(settings.data)
  }, [settings.data])

  const save = useMutation({
    mutationFn: (value: SpeechKitSettings) =>
      saveSpeechKit({
        enabled: value.enabled,
        endpoint: value.endpoint ?? undefined,
        operationEndpoint: value.operationEndpoint ?? undefined,
        folderId: value.folderId ?? '',
        model: value.model ?? undefined,
        ...(apiKey ? { apiKey } : {}),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(['admin-speechkit'], next)
      setApiKey('')
      setNotice(null)
      setSaved(true)
    },
    onError: (failure: unknown) => setNotice(failure instanceof ApiError ? failure.message : t('app.error')),
  })

  if (settings.isError) {
    return <Notice text={settings.error instanceof ApiError ? settings.error.message : t('app.error')} />
  }
  if (!draft) return null
  const set = (patch: Partial<SpeechKitSettings>) => {
    setSaved(false)
    setDraft({ ...draft, ...patch })
  }
  return (
    <section className="panel stack" style={{ gap: 12 }} aria-label={t('speechkit.title')}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h2">{t('speechkit.title')}</span>
        <OnOffBadge on={settings.data?.usable ?? false} />
      </div>
      <p className="t-sm muted" style={{ margin: 0 }}>{t('speechkit.intro')}</p>
      {settings.data?.problem && <Notice text={settings.data.problem} />}
      <form
        className="stack"
        style={{ gap: 12 }}
        aria-label={t('speechkit.title')}
        onSubmit={(event) => {
          event.preventDefault()
          if (mayEdit) save.mutate(draft)
        }}
      >
        <Switch checked={draft.enabled} disabled={!mayEdit} label={t('speechkit.enabled')} onChange={(value) => set({ enabled: value })} />
        <div className="form-grid">
          <Field label={t('speechkit.endpoint')} htmlFor={`${id}-endpoint`}>
            <input id={`${id}-endpoint`} className="input mono" disabled={!mayEdit} value={draft.endpoint ?? ''}
                   onChange={(e) => set({ endpoint: e.target.value })} />
          </Field>
          <Field label={t('speechkit.operationEndpoint')} htmlFor={`${id}-operations`}>
            <input id={`${id}-operations`} className="input mono" disabled={!mayEdit} value={draft.operationEndpoint ?? ''}
                   onChange={(e) => set({ operationEndpoint: e.target.value })} />
          </Field>
          <Field label={t('speechkit.folderId')} htmlFor={`${id}-folder`}>
            <input id={`${id}-folder`} className="input mono" disabled={!mayEdit} value={draft.folderId ?? ''}
                   onChange={(e) => set({ folderId: e.target.value })} />
          </Field>
          <Field label={t('speechkit.model')} htmlFor={`${id}-model`}>
            <input id={`${id}-model`} className="input mono" disabled={!mayEdit} value={draft.model ?? ''}
                   onChange={(e) => set({ model: e.target.value })} />
          </Field>
          <Field label={t('speechkit.apiKey')} htmlFor={`${id}-key`} help={draft.apiKeySet ? t('speechkit.apiKeySet') : undefined}>
            <input id={`${id}-key`} className="input" type="password" autoComplete="new-password" disabled={!mayEdit}
                   value={apiKey} onChange={(e) => {
                     setSaved(false)
                     setApiKey(e.target.value)
                   }} />
          </Field>
        </div>
        <Notice text={notice} />
        {saved && <span className="t-sm muted" role="status">{t('speechkit.saved')}</span>}
        {mayEdit && (
          <div className="row">
            <button type="submit" className="btn btn-primary btn-sm" disabled={save.isPending}>{t('speechkit.save')}</button>
          </div>
        )}
      </form>
    </section>
  )
}
